import { showUpgradeNotice } from "@/lib/planGate";
import axios from "axios";
import { handleUnauthorized } from "@/lib/silentSso";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lv.token";

// The JWT the backend hands back at login/register. Stored in localStorage
// and attached to every XHR below. The backend ALSO mirrors it into an
// httpOnly cookie — that cookie is what authenticates <img>/<audio>/<iframe>
// requests to the GridFS media routes, which can't carry a header.
export const tokenStore = {
    get: () => {
        try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
    },
    set: (t) => {
        try { localStorage.setItem(TOKEN_KEY, t); } catch { /* private mode */ }
    },
    clear: () => {
        try { localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
    },
};

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

const attachToken = (config) => {
    const t = tokenStore.get();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
};
// The text of an API error, always a string. The gateway answers 402 ("not in your plan") with an OBJECT as `detail`
// ({code, message, product, ...}); rendering that object as a React child crashes the page, so callers show this instead.
export const apiDetail = (err, fallback = "") => {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d || fallback;
    if (d && typeof d.message === "string") return d.message;
    return fallback;
};
api.interceptors.request.use(attachToken);
api.interceptors.response.use((r) => r, (err) => {
    if (err.response?.status === 402) showUpgradeNotice(typeof err.response.data?.detail === "object" ? err.response.data.detail : {});
    if (err.response?.status === 401) {
        handleUnauthorized({ url: err.config?.url, method: err.config?.method, hadToken: !!tokenStore.get(),
            setToken: tokenStore.set, clearToken: tokenStore.clear });
    }
    return Promise.reject(err);
});

// For raw fetch() calls that bypass the axios instances.
export const authHeaders = () => {
    const t = tokenStore.get();
    return t ? { Authorization: `Bearer ${t}` } : {};
};

// Not proxied — hikmah-backend's own /auth/* (login, register,
// force-password-update), where the gateway token that the proxy requires
// on every other request is actually issued.
export const gatewayApi = axios.create({ baseURL: process.env.REACT_APP_GATEWAY_URL });
gatewayApi.interceptors.request.use(attachToken);

// --- Auth ---
export const registerEmail = (payload) => gatewayApi.post("/auth/register", payload).then((r) => r.data);
export const loginEmail = (payload) => gatewayApi.post("/auth/login", payload).then((r) => r.data);
export const forcePasswordUpdate = (reset_token, new_password) =>
    gatewayApi.post("/auth/force-password-update", { reset_token, new_password }).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
// Ends the gateway session cookie too (single sign-on) — otherwise the common
// login page would silently sign the user straight back in.
export const gatewayLogout = () =>
  fetch(`${process.env.REACT_APP_GATEWAY_URL}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});

// Sign-out from this app returns to the dashboard's Apps page (same origin as the common login page). The
// gateway session is deliberately kept alive because that page needs it; the dashboard's own "Sign out"
// ends it for every app.
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";
export const leaveToDashboard = () => {
  tokenStore.clear();
  window.location.replace(`${new URL(LOGIN_URL).origin}/dashboard`);
};

// Children are created only in the HikmahVerses dashboard (Profiles); the apps show them under the same name.
export const profilesPageUrl = () => {
  try { return `${new URL(LOGIN_URL).origin}/dashboard?page=profiles`; } catch { return "/"; }
};

export const logoutRequest = () => api.post("/auth/logout").then((r) => r.data).finally(leaveToDashboard);
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);

// Scoped identity swap within one fixed seeded demo group (see
// seed_demo_group/POST /demo/switch-view in server.py) — 403s for any
// non-demo account. Returns the same { token, user } shape as
// login/register, so it plugs straight into AuthContext's adopt().
// 20 weeks (default) = one new poem a week; 40 weeks = one every two weeks.
export const setLearnerPace = (learnerId, pace_weeks) =>
    api.patch(`/learners/${learnerId}/pace`, { pace_weeks }).then((r) => r.data);

export const setClassPace = (containerId, pace_weeks) =>
    api.patch("/teacher/class-pace", { container_id: containerId, pace_weeks }).then((r) => r.data);

export const switchDemoView = (target) =>
    api.post("/demo/switch-view", { target }).then((r) => r.data);

export const ttsAudioUrl = (text, { voice = "onyx", speed } = {}) => {
    let url = `${API}/tts/audio?text=${encodeURIComponent(text)}&voice=${voice}`;
    if (speed) url += `&speed=${speed}`;
    return url;
};
